import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef, NgbPopover, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../alert.service';
import { SettingService } from '../setting.service';
import { faTrashCan, faBold, faItalic, faListOl, faListUl, faCheck, faSave, faPlus, faQuestion, faToggleOn, faToggleOff, faArchive, faTrashRestore, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { AcceptProposalTerm } from '../accept-proposal-term';
import { Tax } from '../tax';
import { AuthService } from '../auth.service';
import dayjs from 'dayjs';
import { FeatureGroupTemplateService } from '../templates/services/feature-group-template.service';
import { LoaderService } from '../shared/services/loader.service';
import { Router, RouterLink } from '@angular/router';
import { ConfirmationModalComponent } from '@main/confirmation-modal/confirmation-modal.component';
import { QuillEditorComponent } from 'ngx-quill';
import { AddressFormComponent } from '../address-form/address-form.component';
import { PhoneMaskDirective } from '../phone-mask.directive';
import { SettingsProposalsAgreementTypesComponent } from '../settings-proposals-agreement-types/settings-proposals-agreement-types.component';
import { NgFor, NgIf, PercentPipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ProposalTermsRequestResponse, ProposalTerm, ProposalTermsAndConditions, TermAndConditionsVersion } from '@app-shared/interfaces/settings/proposal-terms';

@Component({
  selector: 'app-settings-proposals',
  templateUrl: './settings-proposals.component.html',
  styleUrls: ['./settings-proposals.component.css'],
  imports: [FaIconComponent, NgFor, NgIf, SettingsProposalsAgreementTypesComponent, ReactiveFormsModule, PhoneMaskDirective, AddressFormComponent, NgbPopover, NgbTooltip, QuillEditorComponent, PercentPipe]
})
export class SettingsProposalsComponent implements OnInit {

  installationWeeks: { date: string, label: string }[] = [];

  // Modals
  private modalReference: NgbModalRef;

  /* ----- Proposal View ----- */

  // Forms
  editorFormGroup: UntypedFormGroup;
  businessInformationForm: UntypedFormGroup;
  businessAddressForm: UntypedFormGroup;

  displayAddress: boolean;

  /* ----- Taxes ----- */

  @ViewChild('TAX_MODAL', { static: false }) private taxModal;
  taxForm: UntypedFormGroup;
  selectedTax: Tax = null;
  taxes: Tax[] = [];

  /* ----- Accept Proposal Terms ----- */

  // Modals
  @ViewChild('ACCEPT_PROPOSAL_TERM_MODAL', { static: false }) private acceptProposalTermModal;
  @ViewChild('NEW_TERMS_AND_CONDITION_MODAL', { static: false }) private newTermsAndConditionsModal;
  acceptProposalTermForm: UntypedFormGroup;
  selectedAcceptProposalTerm: AcceptProposalTerm = null;
  acceptProposalTerms: AcceptProposalTerm[] = [];
  newTermsAndConditionsForm: UntypedFormGroup;

  /* ----- Proposal Terms ----- */

  proposalTerms: ProposalTerm[] = [];
  selectedProposalTerm: ProposalTerm = null;

  /* ----- Proposal Terms and Conditions ----- */
  proposalTermsAndConditions: ProposalTermsAndConditions[] = [];
  selectedProposalTermAndConditions: ProposalTermsAndConditions = null;
  selectedTermsAndConditionsVersion: TermAndConditionsVersion = null;
  hasCreatedNewTerms: boolean = false;
  hasCreatedNewVersion: boolean = false;
  hasDeletedTemplate: boolean = false;


  /* ----- Notifications ----- */
  onProposalView: boolean = false;
  onProposalAcceptance: boolean = false;
  onProposalRejection: boolean = false;
  sendACopyToSuperAdmin: boolean = false;

  /* ----- Signatures ----- */

  signaturesAreEnabled: boolean = false;

  // Preferences on installation
  preferredInstallation: boolean = false;
  preferredRemoval: boolean = false;
  chosenPreferrences = [];
  /* ----- Other ----- */

  // Font Awesome Imports
  faTrashCan = faTrashCan;
  faBold = faBold;
  faItalic = faItalic;
  faListOl = faListOl;
  faListUl = faListUl;
  faCheck = faCheck;
  faSave = faSave;
  faPlus = faPlus;
  faQuestion = faQuestion;
  faToggleOn = faToggleOn;
  faToggleOff = faToggleOff;
  faArchive = faArchive;
  faTrashRestore = faTrashRestore;
  faCircleCheck = faCircleCheck;

  constructor(private settingService: SettingService,
    private alertService: AlertService,
    private authService: AuthService,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private router: Router,
    private featureGroupTemplateService: FeatureGroupTemplateService
  ) { }

  ngOnInit(): void {
    this.editorFormGroup = new UntypedFormGroup({
      termsAndCondition: new UntypedFormControl(),
      proposalTermTemplate: new UntypedFormControl(),
      proposalTermName: new UntypedFormControl(),
      proposalTermVersion: new UntypedFormControl(),
      multiPricingDescription: new UntypedFormControl()
    });
    this.businessInformationForm = new UntypedFormGroup({
      name: new UntypedFormControl(),
      email: new UntypedFormControl(),
      phone: new UntypedFormControl()
    });
    this.businessAddressForm = new UntypedFormGroup({
      street: new UntypedFormControl(),
      city: new UntypedFormControl(),
      state: new UntypedFormControl('SAS'),
      postalCode: new UntypedFormControl()
    });
    this.acceptProposalTermForm = new UntypedFormGroup({
      content: new UntypedFormControl()
    });
    this.taxForm = new UntypedFormGroup({
      name: new UntypedFormControl(),
      percent: new UntypedFormControl()
    });

    this.newTermsAndConditionsForm = new UntypedFormGroup({
      name: new UntypedFormControl('', Validators.required),
      content: new UntypedFormControl(),
      templateDefault: new UntypedFormControl(false)
    });
    this.getTaxes();
    this.getProposalSettings();
    this.getAcceptProposalTerms();
    this.getMultiPricingDescription();
    this.getProposalTermsAndConditions();
  }

  ngOnDestroy(): void {
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
  }

  // Open Modal
  openModal(content: any): void {
    this.modalReference = this.modalService.open(content);
  }

  /* ----- Proposal View ----- */

  // Get Proposal Settings
  private getProposalSettings(): void {
    this.settingService.getProposalSettings().subscribe((proposalSettings) => {
      // Business Information Form
      this.businessInformationForm.controls.name.setValue(proposalSettings.business.name);
      this.businessInformationForm.controls.email.setValue(proposalSettings.business.email);
      this.businessInformationForm.controls.phone.setValue(proposalSettings.business.phone);
      // Business Address Form
      const address = proposalSettings.business.address;
      this.businessAddressForm.controls.street.setValue(address.street);
      this.businessAddressForm.controls.city.setValue(address.city);
      this.businessAddressForm.controls.state.setValue(address.state);
      this.businessAddressForm.controls.postalCode.setValue(address.postalCode);
      this.displayAddress = proposalSettings.business.displayAddress;
      // Signatures
      this.signaturesAreEnabled = proposalSettings.signaturesAreEnabled;
      // Preferrences
      this.preferredInstallation = proposalSettings.preferredInstallation;

      if (this.preferredInstallation) {
        this.generateInstallationWeeks();
      }
      this.preferredRemoval = proposalSettings.preferredRemoval;
      this.chosenPreferrences = proposalSettings.preferredInstallationOptions?.filter((preferredInstallation) => !preferredInstallation?.date?.includes('2023'));
      this.onProposalView = proposalSettings.notifications.onProposalView;
      this.onProposalAcceptance = proposalSettings.notifications.onProposalAcceptance;
      this.onProposalRejection = proposalSettings.notifications.onProposalRejection;
      this.sendACopyToSuperAdmin = proposalSettings.notifications.sendACopyToSuperAdmin;
    });
  }

  checkIfPrefIsSelected(dateSelected: Date): boolean {
    if (this.chosenPreferrences) {
      return this.chosenPreferrences.some((pref) => pref.date === dateSelected);
    } else {
      this.chosenPreferrences = this.installationWeeks;
      return true;
    }
  }

  // Update Business Information
  updateBusinessInformation(): void {
    if (this.businessInformationForm.valid) {
      this.settingService.updateProposalSettings(this.businessInformationForm.value).subscribe(() => {
        this.alertService.showSuccessAlert('Business Information Updated');
        this.getProposalSettings();
      });
    } else {
      this.businessInformationForm.markAllAsTouched();
    }
  }

  addChosenPreferrences(index) {
    const exists = this.chosenPreferrences.some(week => week.date === this.installationWeeks[index].date);

    if (!exists) {
      this.chosenPreferrences.push(this.installationWeeks[index]);
    } else {
      this.chosenPreferrences = this.chosenPreferrences.filter(pref => pref.date != this.installationWeeks[index].date)
    }
  }

  // Saving preferrence options
  updatePreferredInstallationOptions() {

    if (this.preferredInstallation && this.chosenPreferrences.length != 0) {
      const sortedArr = this.chosenPreferrences.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      this.settingService.updateProposalSettings({ preferredInstallationOptions: sortedArr, preferredInstallation: this.preferredInstallation }).subscribe(() => {
        this.alertService.showSuccessAlert(`Preferred Installation Options are saved`);
      });
    } else if (this.chosenPreferrences.length == 0) {
      this.alertService.showErrorAlert(`Should at least have one Preferred week option`);
    }
  }

  // Update Business Address
  updateBusinessAddress(): void {
    if (this.businessAddressForm.valid) {
      this.settingService.updateProposalSettings({ address: this.businessAddressForm.value }).subscribe(() => {
        this.alertService.showSuccessAlert('Business Address Updated');
        this.getProposalSettings();
      });
    } else {
      this.businessAddressForm.markAllAsTouched();
    }
  }

  // Toggle Display Business Address
  toggleDisplayBusinessAddress(): void {
    this.settingService.updateProposalSettings({ displayAddress: !this.displayAddress }).subscribe(() => {
      this.alertService.showSuccessAlert('Business Address Updated');
      this.getProposalSettings();
    });
  }

  // Business Information Form Accessors
  get businessName() { return this.businessInformationForm.controls.name; }
  get businessEmail() { return this.businessInformationForm.controls.email; }
  get businessPhone() { return this.businessInformationForm.controls.phone; }

  /* ----- Accept Proposal Terms ----- */

  // Get Accept Proposal Terms
  private getAcceptProposalTerms(): void {
    this.settingService.getAcceptProposalTerms().subscribe((acceptProposalTerms) => {
      this.acceptProposalTerms = acceptProposalTerms;
    });
  }

  // Save Accept Proposal Term
  saveAcceptProposalTerm(): void {
    if (this.selectedAcceptProposalTerm === null) {
      this.addAcceptProposalTerm();
    } else {
      this.updateAcceptProposalTerm();
    }
  }

  // Add Accept Proposal Term
  private addAcceptProposalTerm(): void {
    if (this.acceptProposalTermForm.valid) {
      this.settingService.addAcceptProposalTerm(this.acceptProposalTermForm.value.content).subscribe(() => {
        this.alertService.showSuccessAlert('Accept Proposal Term Added');
        this.modalReference.close();
        this.getAcceptProposalTerms();
      });
    } else {
      this.acceptProposalTermForm.markAllAsTouched();
    }
  }

  // Update Accept Proposal Term
  private updateAcceptProposalTerm(): void {
    if (this.acceptProposalTermForm.valid) {
      this.settingService.updateAcceptProposalTerm(this.selectedAcceptProposalTerm.id, this.acceptProposalTermForm.value.content).subscribe(() => {
        this.alertService.showSuccessAlert('Accept Proposal Term Updated');
        this.modalReference.close();
        this.getAcceptProposalTerms();
        this.selectedAcceptProposalTerm = null;
      });
    } else {
      this.acceptProposalTermForm.markAllAsTouched();
    }
  }

  // Delete Accept Proposal Term
  deleteAcceptProposalTerm(): void {
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to delete this accept proposal term?";
    confirmationModalRef.componentInstance.actionBtnTitle = "Delete";
    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.settingService.deleteAcceptProposalTerm(this.selectedAcceptProposalTerm.id).subscribe(() => {
        this.alertService.showSuccessAlert('Accept Proposal Term Deleted');
        this.getAcceptProposalTerms();
        this.selectedAcceptProposalTerm = null;
      });
    });
  }

  // Accept Proposal Term Selected
  acceptProposalTermSelected(acceptProposalTerm: AcceptProposalTerm): void {
    this.selectedAcceptProposalTerm = acceptProposalTerm;
    this.acceptProposalTermForm.controls.content.setValue(acceptProposalTerm.content);
    this.openModal(this.acceptProposalTermModal);
  }

  // Reset Accept Proposal Term Form
  resetAcceptProposalTermForm(): void {
    this.acceptProposalTermForm.reset();
    this.acceptProposalTermForm.controls.content.setValue(null);
  }

  /* ----- Multi Year Agreement Description ----- */

  // Get Multi Pricing Description
  private getMultiPricingDescription(): void {
    this.settingService.getMultiPricingDescription().subscribe((multiPricingDescription) => {
      this.editorFormGroup.controls.multiPricingDescription.setValue(multiPricingDescription?.toString());
    });
  }

  // Update Multi Pricing Description
  updateMultiPricingDescription(): void {
    const multiPricingDescription = this.editorFormGroup.controls.multiPricingDescription.value;
    this.settingService.updateMultiPricingDescription(multiPricingDescription).subscribe(() => {
      this.alertService.showSuccessAlert('Multi Year Agreement Description Updated');
      this.getMultiPricingDescription();
    });
  }

  /* ----- Proposal Terms and Conditions ----- */

  // Get Proposal Terms and Conditions
  private getProposalTermsAndConditions(): void {
    this.settingService.getProposalTermsAndConditions().subscribe((proposalTermsAndConditions) => {
      this.proposalTermsAndConditions = proposalTermsAndConditions;
      if (this.selectedProposalTermAndConditions !== null) {
        setTimeout(() => {
          this.selectProposalTermsAndConditions(this.selectedProposalTermAndConditions.id);
        }, 5);
      } else {
        if (this.proposalTermsAndConditions.length > 0) this.selectProposalTermsAndConditions(this.proposalTermsAndConditions[0].id);
      }
    });
  }

  // Add Proposal Terms and Conditions

  openAddProposalTermsAndConditionsModal(): void {
    this.modalReference = this.modalService.open(this.newTermsAndConditionsModal, { size: 'lg' });
  }

  openTermsConditionsHelp(): void {
    window.open('https://support.tinselcrm.com/knowledge-base/setting-up-your-terms-conditions/', '_blank');
  }

  saveNewTermsAndConditions(event: Event): void {
    event.preventDefault();
    if (this.newTermsAndConditionsForm.valid) {
      this.settingService.addProposalTermsAndConditions(this.newTermsAndConditionsForm.value).subscribe((res: ProposalTermsRequestResponse) => {
        this.alertService.showSuccessAlert('Terms & Conditions Added');
        this.newTermsAndConditionsForm.reset();
        this.hasCreatedNewTerms = true;
        this.modalReference.close();
        this.getProposalTermsAndConditions();
      });
    }
  }

  // Select Proposal Terms And Conditions
  selectProposalTermsAndConditions(proposalTermId: string): void {
    let selectedTemplate = this.proposalTermsAndConditions.find((element) => { return element.id == proposalTermId; });
    this.selectedProposalTermAndConditions = selectedTemplate;
    this.editorFormGroup.controls.proposalTermTemplate.setValue(selectedTemplate?.id);
    this.editorFormGroup.controls.proposalTermName.setValue(selectedTemplate?.name);
    if (this.hasCreatedNewTerms) {
      this.setNewCreatedTerms();
    } else if (this.hasDeletedTemplate) {
      this.setDefaultTemplateAndVesion();
    } else if (this.hasCreatedNewVersion) {
      this.setCreatedNewVersion();
    } else if (this.selectedTermsAndConditionsVersion) {
      this.setDataHasSelectedTermVersion();
    } else {
      const defaultVersion = this.selectedProposalTermAndConditions.versions.find((element) => { return element.isDefault == true; });
      if (!defaultVersion) {
        setTimeout(() => {
          let selectedVersion = this.selectedProposalTermAndConditions.versions[this.selectedProposalTermAndConditions.versions.length - 1];
          this.selectedTermsAndConditionsVersion = selectedVersion;
          this.editorFormGroup.controls.proposalTermVersion.setValue(selectedVersion?.versionNumber);
          this.editorFormGroup.controls.termsAndCondition.setValue(selectedVersion?.content);
        }, 5);
      } else {
        this.selectTermsAndConditionVersion(defaultVersion?.versionNumber);
      }
    }
  }

  setNewCreatedTerms() {
    setTimeout(() => {
      let newTerms = this.proposalTermsAndConditions[this.proposalTermsAndConditions.length - 1];
      let selectedVersion = newTerms?.versions[newTerms?.versions?.length - 1];
      this.selectedProposalTermAndConditions = newTerms;
      this.selectedTermsAndConditionsVersion = selectedVersion;
      this.editorFormGroup.controls.proposalTermTemplate.setValue(newTerms?.id);
      this.editorFormGroup.controls.proposalTermName.setValue(newTerms?.name);
      this.editorFormGroup.controls.proposalTermVersion.setValue(selectedVersion?.versionNumber);
      this.editorFormGroup.controls.termsAndCondition.setValue(selectedVersion?.content);
      this.hasCreatedNewTerms = false;
    }, 5);
  }

  setDefaultTemplateAndVesion() {
    setTimeout(() => {
      let defaultTemplate = this.proposalTermsAndConditions?.find((term) => { return term.isDefault == true; });
      let defaultVersion = defaultTemplate?.versions?.find((version) => { return version.isDefault == true; });
      this.selectedProposalTermAndConditions = defaultTemplate;
      this.selectedTermsAndConditionsVersion = defaultVersion;
      this.editorFormGroup.controls.proposalTermTemplate.setValue(defaultTemplate?.id);
      this.editorFormGroup.controls.proposalTermName.setValue(defaultTemplate?.name);
      this.editorFormGroup.controls.proposalTermVersion.setValue(defaultVersion?.versionNumber);
      this.editorFormGroup.controls.termsAndCondition.setValue(defaultVersion?.content);
      this.hasDeletedTemplate = false;
    }, 5);
  }

  setCreatedNewVersion(): void {
    setTimeout(() => {
      let versionList = this.selectedProposalTermAndConditions.versions;
      let selectedVersion = versionList[versionList.length - 1];
      this.selectedTermsAndConditionsVersion = versionList[versionList.length - 1];
      this.editorFormGroup.controls.proposalTermVersion.setValue(selectedVersion?.versionNumber);
      this.editorFormGroup.controls.termsAndCondition.setValue(selectedVersion?.content);
    }, 5);
  }

  setDataHasSelectedTermVersion(): void {
    setTimeout(() => {
      let selectedVersion = this.selectedProposalTermAndConditions.versions.find((element) => { return element.versionNumber == this.selectedTermsAndConditionsVersion?.versionNumber; });
      if (selectedVersion) {
        this.selectedTermsAndConditionsVersion = selectedVersion;
        this.editorFormGroup.controls.proposalTermVersion.setValue(selectedVersion?.versionNumber);
        this.editorFormGroup.controls.termsAndCondition.setValue(selectedVersion?.content);
      } else {
        const getLastVersion = this.selectedProposalTermAndConditions.versions[this.selectedProposalTermAndConditions.versions.length - 1];
        this.selectedTermsAndConditionsVersion = getLastVersion;
        this.editorFormGroup.controls.proposalTermVersion.setValue(selectedVersion?.versionNumber);
        this.editorFormGroup.controls.termsAndCondition.setValue(selectedVersion?.content);
      }

    }, 5);
  }

  selectTermsAndConditionVersion(versionNumber: number) {
    setTimeout(() => {
      let selectedVersion = this.selectedProposalTermAndConditions.versions.find((element) => { return element.versionNumber == Number(versionNumber); });
      this.selectedTermsAndConditionsVersion = selectedVersion;
      this.editorFormGroup.controls.proposalTermVersion.setValue(selectedVersion?.versionNumber);
      this.editorFormGroup.controls.termsAndCondition.setValue(selectedVersion?.content);
    }, 5);
  }


  get selectedTemplateVersionsList() {
    return this.selectedProposalTermAndConditions?.versions;
  }
  get selectedTemplateVersion() {
    return this.selectedTermsAndConditionsVersion;
  }

  // Set Default Proposal Term and Condtions Template
  setDefaultProposalTermAndCondtionTemplate(): void {
    this.settingService.setDefaultProposalTermAndCondtionTemplate(this.selectedProposalTermAndConditions.id).subscribe(() => {
      this.alertService.showSuccessAlert(`Template <b>${this.selectedProposalTermAndConditions.name}</b> set as default.`);
      this.getProposalTermsAndConditions();
    });
  }

  setDefaultTemplateVersion(): void {
    this.settingService.setDefaultTemplateVersion(this.selectedProposalTermAndConditions.id, this.selectedTermsAndConditionsVersion.versionNumber).subscribe(() => {
      this.alertService.showSuccessAlert(`Version <b>${this.selectedTermsAndConditionsVersion.versionNumber}</b> set as default.`);
      this.getProposalTermsAndConditions();
    });
  }

  updateProposalTermsAndConditions(): void {
    const proposalTerm = {
      id: this.selectedProposalTermAndConditions.id,
      name: this.editorFormGroup.controls.proposalTermName.value,
      content: this.editorFormGroup.controls.termsAndCondition.value,
      versionNumber: this.selectedTermsAndConditionsVersion?.versionNumber
    };

    // CHECK IF IS BEING USED
    this.settingService.checkProposalTermAndConditionIsBeingUsed(proposalTerm.id, proposalTerm.versionNumber).subscribe((res) => {
      if (res.isBeingUsed) {
        this.modalReference = this.modalService.open(ConfirmationModalComponent);
        this.modalReference.componentInstance.title = 'New Version';
        this.modalReference.componentInstance.message = `<b>Version ${proposalTerm.versionNumber}</b> is being used on another proposal and can no longer be edited. This updated version will now be saved as <b>Version ${res.newVersionNumber}</b>.`;
        this.modalReference.componentInstance.actionBtnTitle = 'Continue';
        this.modalReference.componentInstance.actionBtnType = 'PRIMARY';
        this.modalReference.componentInstance.confirmed.subscribe(() => {
          this.updatePoposalTerm(proposalTerm);
          this.hasCreatedNewVersion = true;
        });
      } else {
        this.updatePoposalTerm(proposalTerm);
        this.hasCreatedNewVersion = false;
      }
    })
  }

  updatePoposalTerm(proposalTerm: any): void {
    this.settingService.updateProposalTermsAndConditions(proposalTerm).subscribe(() => {
      this.alertService.showSuccessAlert('Terms & Conditions Updated');
      this.getProposalTermsAndConditions();
      this.modalReference?.close();
    });
  }

  deleteProposalTermAndConditionsTemplate(selectedProposalTermAndConditions: ProposalTermsAndConditions, isDefault: boolean): void {
    if (isDefault) {
      this.modalReference = this.modalService.open(ConfirmationModalComponent);
      this.modalReference.componentInstance.title = 'Delete Template';
      this.modalReference.componentInstance.message = `You cannot delete the default template. Please set another template as default before deleting this one.`;
      this.modalReference.componentInstance.cancelBtnTitle = 'Close';
      this.modalReference.componentInstance.actionBtnIsVisible = false;
    } else {
      this.modalReference = this.modalService.open(ConfirmationModalComponent);
      this.modalReference.componentInstance.title = 'Delete Template';
      this.modalReference.componentInstance.message = `Are you sure you want to delete this template <b>${selectedProposalTermAndConditions.name}</b>?`;
      this.modalReference.componentInstance.actionBtnTitle = 'Continue';
      this.modalReference.componentInstance.actionBtnType = 'DANGER';
      this.modalReference.componentInstance.confirmed.subscribe(() => {
        this.settingService.deleteProposalTermAndConditionsTemplate(selectedProposalTermAndConditions.id).subscribe(() => {
          this.alertService.showSuccessAlert(`Template <b>${selectedProposalTermAndConditions.name}</b> Deleted`);
          this.hasDeletedTemplate = true;
          this.getProposalTermsAndConditions();
          this.modalReference?.close();
        });
      });
    }
  }


  deleteTemplateVersion(selectedTermsAndConditionsVersion: TermAndConditionsVersion, isDefault: boolean): void {
    if (isDefault) {
      this.modalReference = this.modalService.open(ConfirmationModalComponent);
      this.modalReference.componentInstance.title = 'Delete Version';
      this.modalReference.componentInstance.message = `You cannot delete the default version. Please set another version as default before deleting this one.`;
      this.modalReference.componentInstance.cancelBtnTitle = 'Close';
      this.modalReference.componentInstance.actionBtnIsVisible = false;
    } else {
      this.modalReference = this.modalService.open(ConfirmationModalComponent);
      this.modalReference.componentInstance.title = 'Delete Version';
      this.modalReference.componentInstance.message = `Are you sure you want to delete version <b>${selectedTermsAndConditionsVersion.versionNumber}</b>?`;
      this.modalReference.componentInstance.actionBtnTitle = 'Continue';
      this.modalReference.componentInstance.actionBtnType = 'DANGER';
      this.modalReference.componentInstance.confirmed.subscribe(() => {
        this.settingService.deleteTemplateVersion(this.selectedProposalTermAndConditions.id, selectedTermsAndConditionsVersion?.versionNumber).subscribe(() => {
          this.alertService.showSuccessAlert(`Version <b>${selectedTermsAndConditionsVersion.versionNumber}</b> Deleted`);
          this.getProposalTermsAndConditions();
          this.modalReference?.close();
        });
      });
    }
  }


  public archiveProposalTermAndConditionsTemplate(isDefault: boolean) {
    if (isDefault) {
      this.modalReference = this.modalService.open(ConfirmationModalComponent);
      this.modalReference.componentInstance.title = 'Archive Template';
      this.modalReference.componentInstance.message = `You cannot archive the default template. Please set another template as default before archiving this one.`;
      this.modalReference.componentInstance.cancelBtnTitle = 'Close';
      this.modalReference.componentInstance.actionBtnIsVisible = false;
    } else {
      let isArchived = !this.selectedProposalTermAndConditions.isArchived;
      this.settingService.archiveProposalTermAndConditionsTemplate(this.selectedProposalTermAndConditions.id, isArchived).subscribe(() => {
        this.alertService.showSuccessAlert(`Template <b>${this.selectedProposalTermAndConditions.name}</b> ${(this.selectedProposalTermAndConditions.isArchived) ? 'Unarchived' : 'Archived'}`);
        this.getProposalTermsAndConditions();
      });
    }
  }

  public archiveTemplateVersion(isArchived: boolean, isDefault: boolean) {
    if (isDefault) {
      this.modalReference = this.modalService.open(ConfirmationModalComponent);
      this.modalReference.componentInstance.title = 'Archive Version';
      this.modalReference.componentInstance.message = `You cannot archive the default version. Please set another version as default before archiving this one.`;
      this.modalReference.componentInstance.cancelBtnTitle = 'Close';
      this.modalReference.componentInstance.actionBtnIsVisible = false;
    } else {
      this.settingService.archiveTemplateVersion(this.selectedProposalTermAndConditions.id, this.selectedTermsAndConditionsVersion.versionNumber, !isArchived).subscribe(() => {
        this.alertService.showSuccessAlert(`Version <b>${this.selectedTermsAndConditionsVersion.versionNumber}</b> ${(this.selectedTermsAndConditionsVersion.isArchived) ? 'Unarchived' : 'Archived'}`);
        this.getProposalTermsAndConditions();
      });
    }
  }

  // END OF TERMS AND CONDITION CODE

  /* ----- Taxes ----- */

  // Get Taxes
  private getTaxes(): void {
    this.settingService.getTaxes().subscribe((taxes) => {
      this.taxes = taxes;
    });
  }

  // Save Tax
  saveTax(): void {
    if (this.selectedTax === null) {
      this.addTax();
    } else {
      this.updateTax();
    }
  }

  // Add Tax
  private addTax(): void {
    if (this.taxForm.valid) {
      const tax = {
        name: this.taxForm.value.name,
        percent: this.taxForm.value.percent
      };
      this.settingService.addTax(tax).subscribe(() => {
        this.alertService.showSuccessAlert('Tax Added');
        this.modalReference.close();
        this.getTaxes();
      });
    } else {
      this.taxForm.markAllAsTouched();
    }
  }

  // Update Tax
  private updateTax(): void {
    if (this.taxForm.valid) {
      const tax = {
        id: this.selectedTax['id'],
        name: this.taxForm.value.name,
        percent: this.taxForm.value.percent
      };
      this.settingService.updateTax(tax).subscribe(() => {
        this.alertService.showSuccessAlert('Tax Updated');
        this.modalReference.close();
        this.getTaxes();
        this.selectedTax = null;
      });
    } else {
      this.taxForm.markAllAsTouched();
    }
  }

  // Tax Selected
  taxSelected(tax: Tax): void {
    this.selectedTax = tax;
    this.taxForm.controls.name.setValue(tax.name);
    const taxPercent = Number(tax.multiplier * 100).toFixed(3);
    this.taxForm.controls.percent.setValue(taxPercent);
    this.openModal(this.taxModal);
  }

  // Reset Tax Form
  resetTaxForm(): void {
    this.taxForm.reset();
    this.taxForm.controls.name.setValue(null);
    this.taxForm.controls.percent.setValue(null);
  }

  /* ----- Tax Form Accessors ----- */

  get taxName() { return this.taxForm.controls.name; }
  get taxPercent() { return this.taxForm.controls.percent; }

  /* ----- Notifications ----- */
  updateNotifOnProposalView(event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;
    this.settingService.updateProposalSettings({ onProposalView: isEnabled }).subscribe(() => {
      this.alertService.showSuccessAlert(`Proposal View Notification ${(isEnabled) ? 'Enabled' : 'Disabled'}`);
    });
  }
  updateNotifOnProposalAcceptance(event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;
    this.settingService.updateProposalSettings({ onProposalAcceptance: isEnabled }).subscribe(() => {
      this.alertService.showSuccessAlert(`Proposal Acceptance Notification ${(isEnabled) ? 'Enabled' : 'Disabled'}`);
    });
  }
  updateNotifOnProposalRejection(event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;
    this.settingService.updateProposalSettings({ onProposalRejection: isEnabled }).subscribe(() => {
      this.alertService.showSuccessAlert(`Proposal Rejection Notification ${(isEnabled) ? 'Enabled' : 'Disabled'}`);
    });
  }
  updateSendACopyToSuperAdmin(event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;
    this.settingService.updateProposalSettings({ sendACopyToSuperAdmin: isEnabled }).subscribe(() => {
      this.alertService.showSuccessAlert(`Send a Copy to Super Admin ${(isEnabled) ? 'Enabled' : 'Disabled'}`);
    });
  }

  /* ----- Signatures ----- */

  // Update Signature Enabled Status
  updateSignatureEnabledStatus(event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;
    this.settingService.updateProposalSettings({ signaturesAreEnabled: isEnabled }).subscribe(() => {
      this.alertService.showSuccessAlert(`Signatures ${(isEnabled) ? 'Enabled' : 'Disabled'}`);
    });
  }

  // Update Preferred Installation Enabled Status
  updatePreferredInstallationEnabledStatus(event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;
    this.preferredInstallation = isEnabled;

    if (isEnabled) {
      this.generateInstallationWeeks();
    } else {
      this.installationWeeks = [];
      this.settingService.updateProposalSettings({ preferredInstallation: isEnabled }).subscribe(() => {
        this.alertService.showSuccessAlert(`Preferred Installation ${(isEnabled) ? 'Enabled' : 'Disabled'}`);
      });
    }


  }

  // Update Preferred Removal Enabled Status
  updatePreferredRemovalEnabledStatus(event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;
    this.settingService.updateProposalSettings({ preferredRemoval: isEnabled }).subscribe(() => {
      this.alertService.showSuccessAlert(`Preferred Removal ${(isEnabled) ? 'Enabled' : 'Disabled'}`);
    });
  }

  // Generate Installation Weeks
  private generateInstallationWeeks(): void {
    const startDate = dayjs(`${dayjs().year()}-08-01`).startOf('week');
    for (var i = 0; i < 22; i++) {
      const date = dayjs(startDate).add(i, 'week');
      this.installationWeeks.push({
        date: date.format('YYYY-MM-DD'),
        label: `Week of ${date.format('MMMM D, YYYY')}`
      });
    }
  }

  get name() { return this.newTermsAndConditionsForm.controls.name; }

  /* ----- Helper Functions ----- */

  // Bold Selection
  // boldSelection(): void {
  //   const selection = window.getSelection();
  //   const strongElement = `<strong>${selection.toString()}</strong>`;
  //   if (selection.anchorNode.parentElement.nodeName == 'STRONG') {
  //     selection.anchorNode.parentElement.parentElement.innerHTML = selection.anchorNode.parentElement.parentElement.innerHTML.replace(strongElement, selection.toString());
  //   } else {
  //     selection.anchorNode.parentElement.innerHTML = selection.anchorNode.parentElement.innerHTML.replace(selection.toString(), strongElement);
  //   }
  // }

  // Underline Selection
  // underlineSelection(): void {
  //   const selection = window.getSelection();
  //   const strongElement = `<span style="text-decoration: underline;">${selection.toString()}</span>`;
  //   if (selection.anchorNode.parentElement.nodeName == 'SPAN') {
  //     selection.anchorNode.parentElement.parentElement.innerHTML = selection.anchorNode.parentElement.parentElement.innerHTML.replace(strongElement, selection.toString());
  //   } else {
  //     selection.anchorNode.parentElement.innerHTML = selection.anchorNode.parentElement.innerHTML.replace(selection.toString(), strongElement);
  //   }
  // }
}
